import { createTheme } from '@mui/material/styles';

// Path of Exile 2 inspired color palette
const poe2Colors = {
  primary: {
    main: '#00ff00', // Neon green
    light: '#33ff33',
    dark: '#00cc00',
    contrastText: '#000000',
  },
  secondary: {
    main: '#ff6b35', // Orange accent
    light: '#ff8a5c',
    dark: '#e55a2b',
    contrastText: '#000000',
  },
  background: {
    default: '#000000',
    paper: '#0a0a0a',
    elevated: '#1a1a1a',
  },
  text: {
    primary: '#00ff00',
    secondary: '#00cc00',
    disabled: '#666666',
  },
  error: {
    main: '#ff4444',
  },
  warning: {
    main: '#ffaa00',
  },
  success: {
    main: '#00ff00',
  },
  info: {
    main: '#00aaff',
  },
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    ...poe2Colors,
  },
  typography: {
    fontFamily: '"Courier New", "Monaco", "Consolas", monospace',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '2px',
      textShadow: '0 0 10px #00ff00',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      textShadow: '0 0 8px #00ff00',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    h5: {
      fontSize: '1.1rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    body1: {
      fontSize: '1rem',
      letterSpacing: '0.5px',
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: '0.5px',
    },
    button: {
      textTransform: 'uppercase',
      fontWeight: 600,
      letterSpacing: '1px',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: `
            linear-gradient(45deg, transparent 25%, rgba(0, 255, 0, 0.02) 25%),
            linear-gradient(-45deg, transparent 25%, rgba(0, 255, 0, 0.02) 25%),
            linear-gradient(45deg, rgba(0, 255, 0, 0.02) 75%, transparent 75%),
            linear-gradient(-45deg, rgba(0, 255, 0, 0.02) 75%, transparent 75%)
          `,
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
          imageRendering: 'pixelated',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: '#00ff00 #000000',
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: '#000000',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#00ff00',
          borderRadius: '4px',
          border: '2px solid #000000',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#33ff33',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          boxShadow: '0 4px 15px rgba(0, 255, 0, 0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 255, 0, 0.4)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #00ff00, #00cc00)',
          color: '#000000',
          '&:hover': {
            background: 'linear-gradient(45deg, #33ff33, #00ff00)',
          },
        },
        outlined: {
          borderColor: '#00ff00',
          color: '#00ff00',
          '&:hover': {
            borderColor: '#33ff33',
            backgroundColor: 'rgba(0, 255, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0a0a',
          border: '2px solid #00ff00',
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0, 255, 0, 0.2)',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 255, 0, 0.3)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#000000',
            '& fieldset': {
              borderColor: '#00ff00',
            },
            '&:hover fieldset': {
              borderColor: '#33ff33',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00ff00',
              boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#00cc00',
            '&.Mui-focused': {
              color: '#00ff00',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#000000',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00ff00',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#33ff33',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00ff00',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#00ff00',
          '& .MuiSlider-thumb': {
            backgroundColor: '#00ff00',
            border: '2px solid #000000',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
            '&:hover': {
              backgroundColor: '#33ff33',
              boxShadow: '0 0 15px rgba(0, 255, 0, 0.7)',
            },
          },
          '& .MuiSlider-track': {
            backgroundColor: '#00ff00',
            border: 'none',
          },
          '& .MuiSlider-rail': {
            backgroundColor: '#333333',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#00ff00',
            '& + .MuiSwitch-track': {
              backgroundColor: '#00ff00',
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: '#333333',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          color: '#00ff00',
          border: '1px solid #00ff00',
          '&:hover': {
            backgroundColor: '#00ff00',
            color: '#000000',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0a0a',
          border: '1px solid #00ff00',
        },
      },
    },
  },
});
